import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import '../../styles/PayThroughAccountModal.css';

const PayThroughAccountModal = ({
  onClose,
  onConfirm,
  onSplitPayment,
  defaultAccount = null,
  allowSplitPayment = false,
  branch = null,
  registerClosed = false,
}) => {
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(defaultAccount || '');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchPOSAccounts = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (branch) {
        params.branch = branch;
      }
      const response = await axios.get(
        `${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/employees/bank-accounts/pos-accounts`,
        { params }
      );
      setAccounts(response.data);

      if (defaultAccount && response.data.some((acc) => acc.account_number === defaultAccount)) {
        setSelectedAccount(defaultAccount);
      } else if (response.data.length > 0 && !defaultAccount) {
        setSelectedAccount(response.data[0].account_number);
      } else {
        setSelectedAccount('');
      }

      setError(null);
    } catch (err) {
      console.error('Error fetching POS accounts:', err);
      setError('Failed to load payment accounts. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [branch, defaultAccount]);

  useEffect(() => {
    fetchPOSAccounts();
  }, [fetchPOSAccounts]);

  const handleConfirm = () => {
    if (!selectedAccount) {
      alert('Please select a pay through account');
      return;
    }
    onConfirm(selectedAccount);
  };

  const noBankAccountsAvailable = registerClosed && accounts.length === 0;

  return (
    <div className="modal-overlay">
      <div className="pay-through-account-modal">
        <div className="modal-header">
          <h2>Select Pay Through Account</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          {registerClosed && (
            <div className="register-closed-banner">
              Cash register is closed for {branch || 'this branch'}. Only bank payments are allowed.
            </div>
          )}
          {loading ? (
            <div className="loading-message">Loading accounts...</div>
          ) : error ? (
            <div className="error-message">{error}</div>
          ) : noBankAccountsAvailable ? (
            <div className="error-message">
              The cash register is closed and no bank payment accounts are available.
              Please open the register or contact your administrator.
            </div>
          ) : accounts.length === 0 ? (
            <div className="error-message">
              No payment accounts are configured for POS transactions.
              Please contact your administrator.
            </div>
          ) : (
            <>
              <p className="instruction-text">
                Please confirm or select the account through which this payment will be received:
              </p>
              <div className="account-selection">
                {accounts.map((account) => (
                  <label key={account.account_number} className="account-option">
                    <input
                      type="radio"
                      name="account"
                      value={account.account_number}
                      checked={selectedAccount === account.account_number}
                      onChange={(e) => setSelectedAccount(e.target.value)}
                    />
                    <span className="account-info">
                      <span className="account-name">{account.account_name}</span>
                      <span className="account-number">({account.account_number})</span>
                    </span>
                  </label>
                ))}
              </div>
            </>
          )}
        </div>

        <div className="modal-footer">
          {allowSplitPayment && onSplitPayment && (
            <button
              type="button"
              className="btn-split-payment"
              onClick={onSplitPayment}
              disabled={loading || error || accounts.length === 0}
            >
              Split payment
            </button>
          )}
          <div className="modal-footer-actions">
            <button
              className="btn-secondary"
              onClick={onClose}
              type="button"
            >
              Cancel
            </button>
            <button
              className="btn-primary"
              onClick={handleConfirm}
              disabled={!selectedAccount || loading || error || accounts.length === 0}
              type="button"
            >
              Confirm & Continue
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PayThroughAccountModal;
